import { PATH_MAP } from "./PathMap"
import { Permissions } from "../Enum/PermissionsType"
import type { PermissionsType } from "../Enum/PermissionsType"
import type { SystemModuleType } from "../Enum/SystemModuleType"

export type QuickSearchActionGroup =
  | "orders"
  | "money"
  | "products"
  | "services"
  | "company"
  | "fiscal"
  | "whatsapp"
  | "delivery"
  | "help"

export type QuickSearchActionType = "create" | "edit" | "view" | "organize"

export type QuickSearchAction = {
  id: string
  path: string
  group: QuickSearchActionGroup
  type: QuickSearchActionType
  permission?: PermissionsType
  module?: SystemModuleType
  ownerOnly?: boolean
  searchParams?: Record<string, string>
}

export const QUICK_SEARCH_ACTIONS: QuickSearchAction[] = [
  {
    id: "commands",
    path: PATH_MAP.COMMANDS_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeOrders,
  },
  {
    id: "commands_filters",
    path: PATH_MAP.COMMANDS_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeOrders,
    searchParams: { quickAction: "orders-advanced-filters" },
  },
  {
    id: "commands_create",
    path: PATH_MAP.COMMANDS_PAGE,
    group: "orders",
    type: "create",
    permission: Permissions.CanEditeOrders,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "service_points",
    path: PATH_MAP.SERVICE_POINTS_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeServicePoints,
  },
  {
    id: "service_points_create",
    path: PATH_MAP.SERVICE_POINTS_PAGE,
    group: "orders",
    type: "create",
    permission: Permissions.CanEditeServicePoints,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "manual_external_orders",
    path: PATH_MAP.MANUAL_EXTERNAL_ORDERS_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeOrders,
  },
  {
    id: "manual_external_orders_filters",
    path: PATH_MAP.MANUAL_EXTERNAL_ORDERS_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeOrders,
    searchParams: { quickAction: "manual-external-orders-filters" },
  },
  {
    id: "manual_external_orders_create",
    path: PATH_MAP.MANUAL_EXTERNAL_ORDERS_PAGE,
    group: "orders",
    type: "create",
    permission: Permissions.CanEditeOrders,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "products",
    path: PATH_MAP.PRODUCTS_PAGE,
    group: "products",
    type: "view",
    permission: Permissions.CanEditeProducts,
  },
  {
    id: "products_create",
    path: PATH_MAP.PRODUCTS_PAGE,
    group: "products",
    type: "create",
    permission: Permissions.CanEditeProducts,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "products_quick_edit",
    path: PATH_MAP.PRODUCTS_PAGE,
    group: "products",
    type: "edit",
    permission: Permissions.CanEditeProducts,
    searchParams: { quickAction: "products-quick-edit" },
  },
  {
    id: "customers",
    path: PATH_MAP.CUSTOMER_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeClients,
  },
  {
    id: "customers_create",
    path: PATH_MAP.CUSTOMER_PAGE,
    group: "orders",
    type: "create",
    permission: Permissions.CanEditeClients,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "customer_exit_check",
    path: PATH_MAP.CUSTOMER_EXIT_CHECK_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeClients,
  },
  {
    id: "reserved_orders",
    path: PATH_MAP.RESERVED_ORDERS_PAGE,
    group: "orders",
    type: "view",
    permission: Permissions.CanEditeOrders,
  },
  {
    id: "daily_income",
    path: PATH_MAP.DAILY_INCOME_PAGE,
    group: "money",
    type: "view",
    permission: Permissions.CanViewProfit,
  },
  {
    id: "monthly_income",
    path: PATH_MAP.MONTH_INCOME_PAGE,
    group: "money",
    type: "view",
    permission: Permissions.CanViewProfit,
  },
  {
    id: "employee_metrics",
    path: PATH_MAP.EMPLOYEE_METRICS_PAGE,
    group: "money",
    type: "view",
    permission: Permissions.CanViewProfit,
  },
  {
    id: "payroll_summary",
    path: PATH_MAP.PAYROLL_SUMMARY_PAGE,
    group: "money",
    type: "view",
    permission: Permissions.CanViewProfit,
  },
  {
    id: "menu_metrics",
    path: PATH_MAP.MENU_METRICS_PAGE,
    group: "money",
    type: "view",
    permission: Permissions.CanViewProfit,
  },
  { id: "combos", path: PATH_MAP.COMBO_PAGE, group: "products", type: "view", permission: Permissions.CanEditeCombos },
  {
    id: "combos_create",
    path: PATH_MAP.COMBO_PAGE,
    group: "products",
    type: "create",
    permission: Permissions.CanEditeCombos,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "categories",
    path: PATH_MAP.CATEGORY_PAGE,
    group: "products",
    type: "view",
    permission: Permissions.CanEditeCategories,
  },
  {
    id: "categories_create",
    path: PATH_MAP.CATEGORY_PAGE,
    group: "products",
    type: "create",
    permission: Permissions.CanEditeCategories,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "categories_order",
    path: PATH_MAP.CATEGORY_PAGE,
    group: "products",
    type: "organize",
    permission: Permissions.CanEditeCategories,
    searchParams: { quickAction: "category-order" },
  },
  {
    id: "additionals",
    path: PATH_MAP.ADDTIONAL_PAGE,
    group: "products",
    type: "view",
    permission: Permissions.CanEditeAddtionals,
  },
  {
    id: "additionals_create",
    path: PATH_MAP.ADDTIONAL_PAGE,
    group: "products",
    type: "create",
    permission: Permissions.CanEditeAddtionals,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "selections",
    path: PATH_MAP.SELECTION_PAGE,
    group: "products",
    type: "view",
    permission: Permissions.CanEditeAddtionals,
  },
  {
    id: "selections_create",
    path: PATH_MAP.SELECTION_PAGE,
    group: "products",
    type: "create",
    permission: Permissions.CanEditeAddtionals,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "additionals_associate_products",
    path: PATH_MAP.ADDTIONAL_PAGE,
    group: "products",
    type: "edit",
    permission: Permissions.CanEditeAddtionals,
  },
  {
    id: "selections_associate_products",
    path: PATH_MAP.SELECTION_PAGE,
    group: "products",
    type: "edit",
    permission: Permissions.CanEditeAddtionals,
  },
  {
    id: "promotions",
    path: PATH_MAP.PROMOTIONS_PAGE,
    group: "services",
    type: "view",
    permission: Permissions.CanEditePromotions,
  },
  {
    id: "promotions_create",
    path: PATH_MAP.PROMOTIONS_PAGE,
    group: "services",
    type: "create",
    permission: Permissions.CanEditePromotions,
    searchParams: { onboardingCreate: "true" },
  },
  { id: "awards", path: PATH_MAP.AWARDS_PAGE, group: "services", type: "view", permission: Permissions.CanEditeAwards },
  {
    id: "awards_create",
    path: PATH_MAP.AWARDS_PAGE,
    group: "services",
    type: "create",
    permission: Permissions.CanEditeAwards,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "extra_services",
    path: PATH_MAP.SERVICES_PAGE,
    group: "services",
    type: "view",
    permission: Permissions.CanEditeExtraService,
  },
  {
    id: "extra_services_create",
    path: PATH_MAP.SERVICES_PAGE,
    group: "services",
    type: "create",
    permission: Permissions.CanEditeExtraService,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "coupons",
    path: PATH_MAP.COUPONS_PAGE,
    group: "services",
    type: "view",
    permission: Permissions.CanEditeCoupons,
  },
  {
    id: "coupons_create",
    path: PATH_MAP.COUPONS_PAGE,
    group: "services",
    type: "create",
    permission: Permissions.CanEditeCoupons,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "notices",
    path: PATH_MAP.NOTICES_PAGE,
    group: "services",
    type: "view",
    permission: Permissions.CanEditeNotices,
  },
  {
    id: "notices_create",
    path: PATH_MAP.NOTICES_PAGE,
    group: "services",
    type: "create",
    permission: Permissions.CanEditeNotices,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "company_data",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "company-data" },
  },
  {
    id: "social_media",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "social-media" },
  },
  {
    id: "whatsapp_support",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "whatsapp-support" },
  },
  {
    id: "public_menu",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "public-menu" },
  },
  {
    id: "public_menu_customization",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "public-menu-customization" },
  },
  {
    id: "public_menu_qr",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "public-menu-qr" },
  },
  {
    id: "marketing_tracking",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "marketing-tracking" },
  },
  {
    id: "pickup_address",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "address" },
  },
  {
    id: "delivery_shipping",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "shipping" },
  },
  {
    id: "schedule",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "schedule" },
  },
  {
    id: "vacation",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "vacation" },
  },
  {
    id: "operational",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "operational" },
  },
  {
    id: "billing",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "billing" },
  },
  {
    id: "mercado_pago",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "billing" },
  },
  {
    id: "payment_providers",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "billing" },
  },
  {
    id: "tap_to_pay",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "billing" },
  },
  {
    id: "store_groups",
    path: PATH_MAP.STORE_GROUPS_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "store_groups_create",
    path: PATH_MAP.STORE_GROUPS_PAGE,
    group: "company",
    type: "create",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "store-group-create" },
  },
  {
    id: "company_modules",
    path: PATH_MAP.COMPANY_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { onboardingStep: "modules" },
  },
  {
    id: "employees",
    path: PATH_MAP.EMPLOYEES_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanEditeEmployee,
  },
  {
    id: "employees_create",
    path: PATH_MAP.EMPLOYEES_PAGE,
    group: "company",
    type: "create",
    permission: Permissions.CanEditeEmployee,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "print_automation",
    path: PATH_MAP.PRINT_AUTOMATION_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "print_automation_add_agent",
    path: PATH_MAP.PRINT_AUTOMATION_PAGE,
    group: "company",
    type: "create",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "print-agent-create" },
  },
  { id: "plans", path: PATH_MAP.PLANS_PAGE, group: "company", type: "view", permission: Permissions.CanEditeCompany },
  {
    id: "fiscal_integration",
    path: PATH_MAP.FISCAL_INTEGRATION_PAGE,
    group: "fiscal",
    type: "view",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
  },
  {
    id: "fiscal_integration_settings",
    path: PATH_MAP.FISCAL_INTEGRATION_PAGE,
    group: "fiscal",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
    searchParams: { quickAction: "fiscal-settings" },
  },
  {
    id: "fiscal_integration_issuer",
    path: PATH_MAP.FISCAL_INTEGRATION_PAGE,
    group: "fiscal",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
    searchParams: { quickAction: "fiscal-issuer" },
  },
  {
    id: "fiscal_integration_products",
    path: PATH_MAP.FISCAL_INTEGRATION_PAGE,
    group: "fiscal",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
    searchParams: { quickAction: "fiscal-products" },
  },
  {
    id: "fiscal_integration_emit",
    path: PATH_MAP.FISCAL_INTEGRATION_PAGE,
    group: "fiscal",
    type: "create",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
    searchParams: { quickAction: "fiscal-emit" },
  },
  {
    id: "fiscal_integration_documents",
    path: PATH_MAP.FISCAL_INTEGRATION_PAGE,
    group: "fiscal",
    type: "view",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
    searchParams: { quickAction: "fiscal-documents" },
  },
  {
    id: "fiscal_imports",
    path: PATH_MAP.FISCAL_IMPORTS_PAGE,
    group: "fiscal",
    type: "create",
    permission: Permissions.CanEditeCompany,
    ownerOnly: true,
    searchParams: { quickAction: "fiscal-import" },
  },
  {
    id: "whatsapp_agent",
    path: PATH_MAP.WHATSAPP_AUTOMATION_PAGE,
    group: "whatsapp",
    type: "edit",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "whatsapp_agent_pair",
    path: PATH_MAP.WHATSAPP_AUTOMATION_PAGE,
    group: "whatsapp",
    type: "create",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "whatsapp-pair-agent" },
  },
  {
    id: "whatsapp_agent_test",
    path: PATH_MAP.WHATSAPP_AUTOMATION_PAGE,
    group: "whatsapp",
    type: "view",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "whatsapp-test-message" },
  },
  {
    id: "whatsapp_crm",
    path: PATH_MAP.WHATSAPP_CRM_PAGE,
    group: "whatsapp",
    type: "view",
    permission: Permissions.CanAnswerWhatsApp,
  },
  {
    id: "whatsapp_crm_message_settings",
    path: PATH_MAP.WHATSAPP_CRM_PAGE,
    group: "whatsapp",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "whatsapp-crm-message-settings" },
  },
  {
    id: "whatsapp_crm_bot_settings",
    path: PATH_MAP.WHATSAPP_CRM_PAGE,
    group: "whatsapp",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "whatsapp-crm-bot-settings" },
  },
  {
    id: "whatsapp_official",
    path: PATH_MAP.WHATSAPP_OFFICIAL_PAGE,
    group: "whatsapp",
    type: "edit",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "whatsapp_official_meta",
    path: PATH_MAP.WHATSAPP_OFFICIAL_PAGE,
    group: "whatsapp",
    type: "edit",
    permission: Permissions.CanEditeCompany,
    searchParams: { quickAction: "whatsapp-official-meta" },
  },
  {
    id: "resposta_ai_integration",
    path: PATH_MAP.RESPOSTA_AI_INTEGRATION_PAGE,
    group: "whatsapp",
    type: "edit",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "ai_integration",
    path: PATH_MAP.AI_INTEGRATION_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanViewProfit,
  },
  {
    id: "ai_integration_settings",
    path: PATH_MAP.AI_INTEGRATION_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanViewProfit,
    searchParams: { quickAction: "ai-settings" },
  },
  {
    id: "delivery",
    path: PATH_MAP.COURIER_PARTNERS_PAGE,
    group: "delivery",
    type: "view",
    permission: Permissions.CanEditeCourier,
  },
  {
    id: "delivery_create",
    path: PATH_MAP.COURIER_PARTNERS_PAGE,
    group: "delivery",
    type: "create",
    permission: Permissions.CanEditeCourier,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "delivery_link",
    path: PATH_MAP.COURIER_PARTNERS_PAGE,
    group: "delivery",
    type: "create",
    permission: Permissions.CanEditeCourier,
    searchParams: { quickAction: "courier-link" },
  },
  {
    id: "delivery_payments",
    path: PATH_MAP.COURIER_PARTNERS_PAGE,
    group: "delivery",
    type: "view",
    permission: Permissions.CanEditeCourier,
    searchParams: { quickAction: "courier-payments" },
  },
  {
    id: "help_center",
    path: PATH_MAP.HELP_CENTER_PAGE,
    group: "help",
    type: "view",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "push_notifications",
    path: PATH_MAP.PUSH_NOTIFICATIONS_PAGE,
    group: "help",
    type: "view",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "tutorials",
    path: PATH_MAP.TUTORIALS_PAGE,
    group: "help",
    type: "view",
    permission: Permissions.CanEditeCompany,
  },
  {
    id: "my_time_clock",
    path: PATH_MAP.MY_TIME_CLOCK_PAGE,
    group: "company",
    type: "view",
  },
  {
    id: "my_time_clock_adjustment",
    path: PATH_MAP.MY_TIME_CLOCK_PAGE,
    group: "company",
    type: "create",
    searchParams: { quickAction: "time-clock-request-adjustment" },
  },
  {
    id: "time_clock",
    path: PATH_MAP.TIME_CLOCK_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanEditeTimeClock,
  },
  {
    id: "time_clock_adjustments",
    path: PATH_MAP.TIME_CLOCK_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeTimeClock,
    searchParams: { quickAction: "time-clock-adjustments" },
  },
  {
    id: "time_clock_reports",
    path: PATH_MAP.TIME_CLOCK_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanEditeTimeClock,
    searchParams: { quickAction: "time-clock-reports" },
  },
  {
    id: "time_clock_settings",
    path: PATH_MAP.TIME_CLOCK_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanEditeTimeClock,
    searchParams: { quickAction: "time-clock-settings" },
  },
  {
    id: "my_checklists",
    path: PATH_MAP.MY_CHECKLISTS_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanExecuteChecklists,
  },
  {
    id: "checklists",
    path: PATH_MAP.CHECKLISTS_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanManageChecklists,
  },
  {
    id: "checklists_create",
    path: PATH_MAP.CHECKLISTS_PAGE,
    group: "company",
    type: "create",
    permission: Permissions.CanManageChecklists,
    searchParams: { onboardingCreate: "true" },
  },
  {
    id: "checklists_sectors",
    path: PATH_MAP.CHECKLISTS_PAGE,
    group: "company",
    type: "edit",
    permission: Permissions.CanManageChecklists,
    searchParams: { quickAction: "checklists-sectors" },
  },
  {
    id: "checklists_history",
    path: PATH_MAP.CHECKLISTS_PAGE,
    group: "company",
    type: "view",
    permission: Permissions.CanManageChecklists,
    searchParams: { quickAction: "checklists-history" },
  },
]
